import {
  useWSModalCustom,
  WSControl,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInfoBox,
  WSInputTextarea,
  WSList,
  WSModal,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { ErrorAlert } from "@components/ErrorAlert";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { queriesPayeeRows } from "../../../../v3/query/queriesPayeeRows";
import { queriesRequirements } from "../../../../v3/query/queriesRequirements";
import { useXWingspanPayerId } from "../../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { selectorIsRequirementWithApprovalFlow } from "../../selectors/selectorIsRequirementWithApprovalFlow";
import {
  ContextItem,
  RequirementAdditionalData,
  RequirementStatusViewProps
} from "../../types";
import { getIsGeneralRequirement } from "../../utils";
import { isEmployeeTaxFormsRequirementType } from "../../modules/EmployeeTaxForms/constants";

interface MarkRequirementAsCompleteModalProps {
  onClose: () => void;
  requirement: PayeeRequirement;
  context: ContextItem;
  additionalData?: RequirementAdditionalData;
  currentStatus: RequirementStatusViewProps;
  onSuccess: () => void;
}

type FormData = {
  reason: string;
  reRequest: boolean;
};

const MarkRequirementAsIncompleteModal: React.FC<
  MarkRequirementAsCompleteModalProps
> = ({ onClose, requirement, context, onSuccess, currentStatus }) => {
  const [rejectRequirement, rejectRequirementMeta] =
    queriesRequirements.rejectPayeeRequirement.useMutation();

  const [resetRequirement, resetRequirementMeta] =
    queriesRequirements.resetPayeeRequirement.useMutation();

  const xWingspanAccount = useXWingspanExpansion();
  const xWingspanPayerId = useXWingspanPayerId();
  const queryPayeeGeneralSummary = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        requirements_requirementId: { eq: requirement.id }
      },
      page: { size: 1 },
      xWingspanAccount
    },
    { select: list => list.pagination?.totalSize }
  );

  const onSubmit = (formData: FormData) => {
    rejectRequirement(
      {
        requirementId: requirement.id!,
        rejectPayeeRequirementRequest: { reason: formData.reason },
        xWingspanAccount: xWingspanPayerId
      },
      {
        onSuccess() {
          if (formData.reRequest) {
            resetRequirement(
              {
                requirementId: requirement.id!,
                xWingspanAccount: xWingspanPayerId
              },
              {
                onSuccess() {
                  onSuccess();
                  onClose();
                }
              }
            );
          } else {
            onSuccess();
            onClose();
          }
        }
      }
    );
  };

  const contractorsLength = queryPayeeGeneralSummary.data || "-";

  const isApprovalFlow = selectorIsRequirementWithApprovalFlow(requirement);

  // EmployeeTaxForms has no working reset — resetPayeeRequirement immediately
  // re-derives Complete (or 409s), undoing the mark-incomplete; hide re-request
  // until the forms service exposes a filings reset (deferred per review).
  const isReRequestAvailable = !isEmployeeTaxFormsRequirementType(
    requirement.type
  );
  const isGeneralRequirement = getIsGeneralRequirement(requirement);

  return (
    <WSForm<FormData> defaultValues={{ reason: "" }} onSubmit={onSubmit}>
      <WSModal
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: onClose,
              type: "button"
            },
            {
              kind: "Primary",
              label: isApprovalFlow ? "Yes, reject" : "Yes, mark incomplete",
              type: "submit",
              loading:
                rejectRequirementMeta.isLoading ||
                resetRequirementMeta.isLoading
            }
          ]
        }}
        title={
          isApprovalFlow
            ? "Reject requirement"
            : "Mark requirement as incomplete"
        }
        onClose={onClose}
      >
        <WSList gap="XL">
          <WSFlexBox gap="M" wrap="nowrap">
            <WSElement>
              <WSText.ParagraphSm color="gray600" mb="S">
                Current status:
              </WSText.ParagraphSm>
              <WSPill {...currentStatus.pill}></WSPill>
              <WSText.ParagraphXs color="gray400" ml="S" mt="XS">
                {currentStatus.secondaryText}
              </WSText.ParagraphXs>
            </WSElement>
            <WSIcon
              name="arrow-right"
              style={{
                marginTop: "auto",
                marginBottom: "auto"
              }}
            />
            <WSForm.Value name="reRequest">
              {(isReRequest: boolean) => (
                <WSElement>
                  <WSText.ParagraphSm color="gray600" mb="S">
                    Overwritten status preview:
                  </WSText.ParagraphSm>
                  {isGeneralRequirement ? (
                    <WSPill icon="time" text="Pending" theme="warning" />
                  ) : (
                    <WSPill
                      icon="time"
                      text="Awaiting contractor"
                      theme="neutral"
                    />
                  )}
                  <WSText.ParagraphXs color="gray400" ml="S" mt="XS">
                    {isReRequest
                      ? "Awaiting contractor completion"
                      : isApprovalFlow
                        ? "Rejected by you"
                        : "Marked incomplete by you"}
                  </WSText.ParagraphXs>
                </WSElement>
              )}
            </WSForm.Value>
          </WSFlexBox>

          <WSList gap="L">
            {!isGeneralRequirement && isReRequestAvailable ? (
              <WSElement>
                <WSText.ParagraphSm color="gray500">
                  Re-request submission (optional)
                </WSText.ParagraphSm>
                <WSText.ParagraphXs color="gray400" mb="S" mt="XS">
                  Check the box to reset the status and request a new submission
                  by the contractor.
                </WSText.ParagraphXs>
                <WSForm.Field
                  component={WSControl}
                  componentProps={{
                    type: "checkbox"
                  }}
                  label="Re-request submission"
                  name="reRequest"
                />
              </WSElement>
            ) : null}

            <WSElement>
              <WSForm.Field
                component={WSInputTextarea}
                componentProps={{
                  helperText:
                    "Please provide specific details that will inform the contractor about the rejection",
                  placeholder: "Provide specific details about the decision"
                }}
                label="Comment (optional)"
                name="reason"
              />
            </WSElement>
          </WSList>

          <WSList gap="L">
            <WSInfoBox>
              {isGeneralRequirement ? (
                <WSText.ParagraphSm color="gray600">
                  By proceeding, the requirement status will be marked
                  incomplete by you.
                  <ul>
                    <li>
                      The status will be updated to “Pending” (marked incomplete
                      by you)
                    </li>
                    <li>
                      This is a system-calculated requirement, if you are
                      marking it as incomplete contractors won’t be able to
                      re-submit the requirement.
                    </li>
                    <li>
                      The decision and status updates will be tracked in the
                      requirement history
                    </li>
                  </ul>
                </WSText.ParagraphSm>
              ) : (
                <WSText.ParagraphSm color="gray600">
                  By proceeding, the requirement status will be marked
                  incomplete by you.
                  <ul>
                    <li>
                      The status will be updated to “Awaiting contractor”
                      (requiring contractor action)
                    </li>
                    <li>
                      Optionally, you can re-request the contractor to take
                      action once again to fulfill the requirement (such as
                      upload a new file)
                    </li>
                    <li>
                      Provide a reason and comments to the contractor regarding
                      this decision
                    </li>
                    <li>
                      The decision and status updates will be tracked in the
                      requirement history
                    </li>
                  </ul>
                </WSText.ParagraphSm>
              )}
              <WSText.ParagraphSm color="gray600">
                This requirement is assigned to {contractorsLength} of the
                contractor’s engagements and may affect their eligibility to
                receive payments from you. Would you like to proceed?
              </WSText.ParagraphSm>
            </WSInfoBox>
            <WSInfoBox>
              <WSText.ParagraphSm color="gray600">
                If you are looking to stop working with the contractor on an
                engagement or stop working with the contractor entirely and
                disregard the requirement(s) altogether, go to the contractor
                profile and archive the contractor engagement or the contractor
                entirely.
              </WSText.ParagraphSm>
            </WSInfoBox>
          </WSList>
          <ErrorAlert
            context="Other"
            error={
              rejectRequirementMeta.isError || resetRequirementMeta.isError
            }
            mt="XL"
          />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useMarkRequirementAsIncompleteModal = () =>
  useWSModalCustom(MarkRequirementAsIncompleteModal);
